import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bulkUploadExclusionRules,
  createExclusionRule,
  deleteExclusionRules,
  getExclusionRule,
  getExclusionRules,
  updateExclusionRule,
} from "controller/api/control-center/channels/emailExclusionRules";
import type {
  ApiErrorType,
  ExclusionRuleInput,
  ExclusionRulesListQuery,
} from "shared";
import { sentryLogger } from "utilz";

const EXCLUSION_RULES_KEY = ["email-exclusion-rules"] as const;
const EXCLUSION_RULES_LIST_KEY = [...EXCLUSION_RULES_KEY, "list"] as const;
const EXCLUSION_RULE_DETAIL_KEY = (ruleId: string) =>
  [...EXCLUSION_RULES_KEY, "detail", ruleId] as const;

const logError = (functionName: string) => (error: ApiErrorType) =>
  sentryLogger({
    error,
    workflow: "controlCenter",
    level: "error",
    functionName,
  });

/** Every write can change any page / filter of the list, and the rule detail. */
const useInvalidateExclusionRules = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: EXCLUSION_RULES_KEY });
};

export const useExclusionRulesList = (query: ExclusionRulesListQuery) =>
  useQuery({
    queryKey: [
      ...EXCLUSION_RULES_LIST_KEY,
      query.page,
      query.per_page,
      query.sort_by,
      query.sort_dir,
      query.condition_type,
      query.action,
      query.applies_to,
      query.search,
    ],
    queryFn: () => getExclusionRules(query),
    keepPreviousData: true,
    staleTime: 30_000,
    onError: logError("getExclusionRules"),
  });

export const useExclusionRuleDetail = (ruleId: string | null) =>
  useQuery({
    queryKey: EXCLUSION_RULE_DETAIL_KEY(ruleId ?? ""),
    queryFn: () => getExclusionRule(ruleId ?? ""),
    enabled: Boolean(ruleId),
    // The edit drawer prefills from this — never edit off a stale copy.
    staleTime: 0,
    onError: logError("getExclusionRule"),
  });

const isApiError = (error: unknown): error is ApiErrorType =>
  typeof error === "object" &&
  error !== null &&
  "status" in error &&
  "message" in error;

/**
 * One POST per rule — the API creates a single rule per call. Resolves with
 * one settled result per payload, in order, so the caller can tell which rows
 * failed (a 409 duplicate on one row must not hide the others' success).
 */
export const useCreateExclusionRules = () => {
  const invalidate = useInvalidateExclusionRules();
  return useMutation({
    mutationFn: async (payloads: ExclusionRuleInput[]) => {
      const results = await Promise.allSettled(
        payloads.map((payload) => createExclusionRule(payload)),
      );
      for (const result of results) {
        if (result.status === "rejected" && isApiError(result.reason)) {
          logError("createExclusionRule")(result.reason);
        }
      }
      return results;
    },
    onSuccess: invalidate,
  });
};

export const useUpdateExclusionRule = () => {
  const invalidate = useInvalidateExclusionRules();
  return useMutation({
    mutationFn: ({
      ruleId,
      payload,
    }: {
      ruleId: string;
      payload: ExclusionRuleInput;
    }) => updateExclusionRule(ruleId, payload),
    onSuccess: invalidate,
    onError: logError("updateExclusionRule"),
  });
};

export const useDeleteExclusionRules = () => {
  const invalidate = useInvalidateExclusionRules();
  return useMutation({
    mutationFn: (ids: string[]) => deleteExclusionRules(ids),
    onSuccess: invalidate,
    onError: logError("deleteExclusionRules"),
  });
};

export const useBulkUploadExclusionRules = () => {
  const invalidate = useInvalidateExclusionRules();
  return useMutation({
    mutationFn: (file: File) => bulkUploadExclusionRules(file),
    onSuccess: invalidate,
    onError: logError("bulkUploadExclusionRules"),
  });
};
